import React, { useState } from 'react';
import { X } from 'lucide-react';
import { CircleAlert as AlertCircle } from 'lucide-react';
import Webcam from 'react-webcam';
import Draggable from 'react-draggable';
import { BackgroundBlurVideo } from './BackgroundBlurVideo';

interface DraggableVideoPreviewProps {
  onClose: () => void;
  isRecording?: boolean;
  videoRef?: React.RefObject<HTMLVideoElement>;
  useBackgroundBlur?: boolean;
  mirrored?: boolean;
  fullScreen?: boolean;
  onStreamReady?: (stream: MediaStream) => void;
  onCameraError?: (error: string) => void;
  videoConstraints?: MediaTrackConstraints;
}

export const DraggableVideoPreview: React.FC<DraggableVideoPreviewProps> = ({
  onClose,
  isRecording = false,
  videoRef,
  useBackgroundBlur = false,
  mirrored = true,
  onStreamReady,
  onCameraError,
  fullScreen = false,
  videoConstraints,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [waitingForPermission, setWaitingForPermission] = useState(false);

  // Determine styling based on fullScreen prop
  const containerStyle = fullScreen 
    ? { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20 } as React.CSSProperties
    : { zIndex: 50 } as React.CSSProperties;
  
  const videoContainerClass = fullScreen
    ? "w-full h-full"
    : "w-64 bg-tsl-purple-900 rounded-lg overflow-hidden shadow-lg border-2 border-tsl-accent";
    
  const headerClass = fullScreen
    ? "hidden drag-handle absolute top-0 left-0 w-full h-12 cursor-grab"
    : "drag-handle bg-tsl-purple-800 px-3 py-2 flex items-center justify-between cursor-grab";

  return (
    <Draggable
      handle=".drag-handle"
      bounds={fullScreen ? "body" : "parent"}
      defaultPosition={fullScreen ? {x: 0, y: 0} : {x: 20, y: 20}}
    >
      <div className={`fixed ${videoContainerClass}`} style={containerStyle}>
        {/* Draggable Header */}
        <div className={headerClass}>
          {!fullScreen && (
            <>
              <span className="text-sm font-medium text-white/90">Camera Preview</span>
              <button
                onClick={onClose}
                className="p-1 hover:bg-tsl-purple-700 rounded-full text-white/70 hover:text-white"
              >
                <X size={16} />
              </button>
            </>
          )}
        </div>

        {/* Loading Indicator */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-tsl-purple-900/90 z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-tsl-accent mx-auto mb-2"></div>
              {waitingForPermission && (
                <p className="text-sm text-white/70">Waiting for camera permission...</p>
              )}
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-tsl-purple-900/90 z-10 p-4">
            <div className="text-center text-red-400">
              <AlertCircle className="mx-auto mb-2" size={24} />
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Video Preview */}
        <div className={`relative ${fullScreen ? 'h-full' : 'aspect-video'}`}>
          {videoRef ? (
            <video
              ref={videoRef}
              autoPlay
              muted
              className="w-full h-full object-cover bg-black"
              style={{ transform: mirrored ? 'scaleX(-1)' : 'none' }}
            />
          ) : useBackgroundBlur ? (
            <BackgroundBlurVideo 
              onLoad={() => setIsLoading(false)}
              onError={(err) => {
                console.error('Background blur error:', err);
                setError('Failed to initialize background blur. Falling back to normal camera.');
                setIsLoading(false);
              }}
              width={fullScreen ? window.innerWidth : 320}
              height={fullScreen ? window.innerHeight : 180}
              blurAmount={20}
              mirrored={mirrored}
              className="w-full h-full object-cover"
            />
          ) : (
            <Webcam
              key={retryCount}
              audio={false}
              mirrored={mirrored}
              className="w-full h-full object-cover bg-black"
              videoConstraints={videoConstraints || {
                facingMode: 'user'
              }}
              onUserMedia={(stream) => {
                console.log('Camera stream ready:', stream);
                setIsLoading(false);
                setError(null);
                setWaitingForPermission(false);
                if (onStreamReady) {
                  onStreamReady(stream);
                }
              }}
              onUserMediaError={(err) => {
                console.error('Webcam error:', err);

                if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                  setWaitingForPermission(true);
                  setError(null);

                  if (retryCount < 5) {
                    setTimeout(() => {
                      console.log('Retrying camera access, attempt:', retryCount + 1);
                      setRetryCount(prev => prev + 1);
                    }, 1500);
                  } else {
                    const msg = 'Camera permission denied. Please allow camera access in your browser settings and refresh the page.';
                    setError(msg);
                    setIsLoading(false);
                    setWaitingForPermission(false);
                    onCameraError?.(msg);
                  }
                } else {
                  const msg = 'Failed to access camera. Please check your camera connection.';
                  setError(msg);
                  setIsLoading(false);
                  setWaitingForPermission(false);
                  onCameraError?.(msg);
                }
              }}
            />
          )}
          
          {/* Recording Indicator */}
          {isRecording && (
            <div className="absolute top-2 right-2 flex items-center gap-2 bg-black/50 px-2 py-1 rounded-full">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs font-medium">{fullScreen ? "RECORDING" : "REC"}</span>
            </div>
          )}
        </div>
      </div>
    </Draggable>
  );
};